import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Card, CardContent, CardHeader, MenuItem, Stack, TextField, Button } from '@mui/material';
import { useUser } from '../../contexts/UserContext';
import { useCart } from '../../contexts/CartContext';
import ChildrenAccordion from '../children/ChildrenAccordion';
import ActionConfirmation from '../elements/ActionConfirmation';
import type { ActionConfirmationVariant } from '../../types';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { CHILD_DOB_KEY } from '../../constants';
import { useScrollToConfirmation } from '../../hooks/useScrollToConfirmation';

interface ChildrenFormProps {
    onChildrenDataChange?: (data: any[]) => void;
}

export interface ChildrenFormHandle {
    addPendingChildIfValid: () => Promise<boolean>;
    getChildren: () => any[];
}

const ChildrenForm = forwardRef(
    ({ onChildrenDataChange }: ChildrenFormProps, ref: React.Ref<ChildrenFormHandle>) => {
    const { user, saveUser, updateChildInUser } = useUser();
    const { fetchCart } = useCart();
    const [children, setChildren] = useState(user?.children || []);
    const [confirmation, setConfirmation] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({
        visible: false,
        variant: 'success',
        message: '',
    });
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [childData, setChildData] = useState({
        fname: '',
        lname: '',
        dob: '',
        gender: '',
        isNew: true,
    });
    const maxYearOld = dayjs().subtract(17, 'year');

    const genders = ['Male', 'Female', 'Other'];

    useEffect(() => {
        if (user?.children) {
            setChildren(user.children);
        }
    }, [user]);

    useEffect(() => {
        if (onChildrenDataChange) {
            onChildrenDataChange(children);
        }
    }, [children, onChildrenDataChange]);

    useScrollToConfirmation(confirmation.visible, confirmation.message, 'children-form-confirmation');

    const handleChildDataChange = (key: string, value: string | Dayjs | null) => {
        if (key === CHILD_DOB_KEY) {
            const formattedDate = value ? (typeof value === 'string' ? value : value.format('MM/DD/YYYY')) : '';
            setChildData(prevData => ({
                ...prevData,
                [key]: formattedDate,
            }));
        } else {
            setChildData(prevData => ({
                ...prevData,
                [key]: value,
            }));
        }

        return;
    };

    const handleDismissConfirmation = () => {
        setConfirmation((c) => ({ ...c, visible: false }));
    };

    const handleToggleForm = () => {
        setIsFormOpen(!isFormOpen);
        if (isFormOpen) {
            setChildData({ fname: '', lname: '', dob: '', gender: '', isNew: true });
        }
    };

    const handleAddChild = async () => {
        // Ensure the child form is visible so validation messages are seen.
        setIsFormOpen(true);

        if (!childData.fname || !childData.lname || !childData.dob || !childData.gender) {
            setConfirmation({ visible: true, variant: 'error', message: 'All fields are required.' });
            return false;
        }

        // Validate date of birth: must be valid and within allowed range
        const dobParsed = dayjs(childData.dob, 'MM/DD/YYYY', true);
        if (!dobParsed.isValid()) {
            setConfirmation({ visible: true, variant: 'error', message: 'Please enter a valid date of birth (MM/DD/YYYY).' });
            return false;
        }
        if (dobParsed.isAfter(dayjs())) {
            setConfirmation({ visible: true, variant: 'error', message: 'Date of birth cannot be in the future.' });
            return false;
        }
        if (dobParsed.isBefore(maxYearOld)) {
            setConfirmation({ visible: true, variant: 'error', message: 'Date of birth must be no more than 17 years ago.' });
            return false;
        }

        // Check for duplicate child
        const isDuplicate = children.some(
            (child) =>
                child.fname.toLowerCase() === childData.fname.toLowerCase() &&
                child.lname.toLowerCase() === childData.lname.toLowerCase() &&
                child.dob === childData.dob
        );

        if (isDuplicate) {
            setConfirmation({ visible: true, variant: 'error', message: 'A child with the same name and date of birth already exists.' });
            return false;
        }

        // Create new child with temporary ID
        const newChild = {
            id: Date.now(),
            ...childData,
            gender: childData.gender.toLowerCase(),
            isNew: true,
        };

        // Add to local state
        const updatedChildren = [...children, newChild];
        setChildren(updatedChildren);

        // In sign-up flow (children managed by parent component), just "confirm" the child locally.
        if (onChildrenDataChange) {
            handleToggleForm();
            setConfirmation({ visible: true, variant: 'success', message: 'Child added.' });
            setChildData({
                fname: '',
                lname: '',
                dob: '',
                gender: '',
                isNew: true,
            });
            return true;
        }
        // In profile flow, immediately persist to API via user context.
        setIsSaving(true);
        const success = await saveUser({ ...user!, children: updatedChildren });
        setIsSaving(false);

        if (success) {
            setChildren(prev => prev.map(c => c.id === newChild.id ? { ...c, isNew: false } : c));
            handleToggleForm();
            setConfirmation({ visible: true, variant: 'success', message: 'Child added successfully.' });
            setChildData({
                fname: '',
                lname: '',
                dob: '',
                gender: '',
                isNew: true,
            });
            return true;
        } else {
            setChildren(prev => prev.filter(c => c.id !== newChild.id));
            setConfirmation({ visible: true, variant: 'error', message: 'Failed to save child. Please try again.' });
            return false;
        }
    };

    useImperativeHandle(ref, () => ({
        addPendingChildIfValid: handleAddChild,
        getChildren: () => children,
    }));

    const handleDeleteChild = (childId: number) => {
        setChildren(currentChildren => currentChildren.filter(child => child.id !== childId));
    };

    /** Updates child details in local state and context. API integration can be added later. */
    const handleUpdateChild = async (childId: number, updates: { fname?: string; lname?: string; dob?: string }) => {
        if (updates.dob !== undefined) {
            const dobParsed = dayjs(updates.dob, 'MM/DD/YYYY', true);
            if (!dobParsed.isValid()) {
                setConfirmation({ visible: true, variant: 'error', message: 'Please enter a valid date of birth (MM/DD/YYYY).' });
                return;
            }
            if (dobParsed.isAfter(dayjs()) || dobParsed.isBefore(maxYearOld)) {
                setConfirmation({ visible: true, variant: 'error', message: 'Date of birth must be between 17 years ago and today.' });
                return;
            }
        }

        // Optimistically update local state
        setChildren(currentChildren =>
            currentChildren.map(child =>
                child.id === childId ? { ...child, ...updates } : child
            )
        );

        const success = await updateChildInUser(childId, updates, async () => {
            await fetchCart();
        });

        if (success) {
            setConfirmation({ visible: true, variant: 'success', message: 'Child details updated.' });
        } else {
            // Rollback optimistic update on failure
            if (user?.children) {
                setChildren(user.children);
            }
            setConfirmation({ visible: true, variant: 'error', message: 'Failed to update child. Please try again.' });
        }
    };

    const existingChildrenCount = children.filter(child => !child.isNew).length;
    const title = existingChildrenCount > 1 ? 'Child Profiles' : 'Child Profile';
    const buttonText = children.length > 0 ? '+ Add Another Child' : '+ Add Child';

    return (
        <>
            <ActionConfirmation
                id="children-form-confirmation"
                visible={confirmation.visible}
                variant={confirmation.variant}
                message={confirmation.message}
                onDismiss={handleDismissConfirmation}
            />
            <Card elevation={2} className="single-form">
                <CardHeader 
                    title={title}
                    titleTypographyProps={{ variant: 'h5', fontWeight: 600 }}
                    sx={{
                        backgroundColor: 'rgba(2, 167, 155, 0.12)',
                        borderBottom: '1px solid rgba(2, 167, 155, 0.3)',
                    }}
                />
                <CardContent sx={{ p: 3 }}>
                    {children.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Stack spacing={1.5}>
                                {children.map(child => (
                                    <ChildrenAccordion
                                        key={child.id}
                                        {...child}
                                        genders={genders}
                                        onDelete={() => handleDeleteChild(child.id)}
                                        onUpdate={handleUpdateChild}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {!isFormOpen && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleToggleForm}
                                sx={{
                                    backgroundColor: '#88B73E',
                                    color: 'white',
                                    '&:hover': { backgroundColor: '#7aa635' },
                                }}
                            >
                                {buttonText}
                            </Button>
                        </Box>
                    )}
                    {isFormOpen && (
                        <Stack spacing={2}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                variant="outlined"
                                value={childData.fname}
                                onChange={(e) => handleChildDataChange('fname', e.target.value)}
                                inputProps={{ maxLength: 30 }}
                                sx={{ '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                variant="outlined"
                                value={childData.lname}
                                onChange={(e) => handleChildDataChange('lname', e.target.value)}
                                inputProps={{ maxLength: 30 }}
                                sx={{ '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Date of Birth"
                                    value={childData.dob && dayjs(childData.dob, 'MM/DD/YYYY').isValid() ? dayjs(childData.dob, 'MM/DD/YYYY') : null}
                                    onChange={(date) => handleChildDataChange('dob', date)}
                                    format="MM/DD/YYYY"
                                    maxDate={dayjs()}
                                    minDate={maxYearOld}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            fullWidth: true,
                                            required: true,
                                            sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } },
                                        }
                                    }}
                                    onError={(reason) => {
                                        if (reason === 'minDate') {
                                            console.error('Date of Birth must be no more than 17 years old.');
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                            <TextField
                                required
                                fullWidth
                                select
                                label="Gender"
                                variant="outlined"
                                value={childData.gender}
                                onChange={(e) => handleChildDataChange('gender', e.target.value)}
                                sx={{ '& .MuiFormLabel-asterisk': { color: 'error.main' } }}
                            >
                                {genders.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, pt: 1 }}>
                                <Button
                                    className="register-button gray"
                                    variant="contained"
                                    onClick={handleToggleForm}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!childData.fname || !childData.lname || !childData.dob || !childData.gender || isSaving}
                                    onClick={handleAddChild}
                                    sx={{
                                        backgroundColor: '#88B73E',
                                        color: 'white',
                                        '&:hover': { backgroundColor: '#7aa635' },
                                        '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.26)', color: 'rgba(0, 0, 0, 0.38)' }
                                    }}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </>
    );
});

export default ChildrenForm;
